/**
 * Per-bar value series (Pine's "series" model). Every piece of indicator state lives in a series
 * indexed by bar, and a node computing bar `i` reads its inputs at `≤ i` and its own series at
 * `< i` only. That makes recomputing a bar **idempotent**, which is the whole incremental story:
 * a tick re-runs the last bar, a new bar runs one more index, a history change re-runs from 0, and
 * all three give bit-identical results to a full recompute (property-tested).
 *
 * `NaN` is Pine's `na` (warm-up, gaps, "no value").
 */

/** Read-only view of a per-bar series. Out-of-range indices read NaN. */
export interface Series {
  at(i: number): number;
}

/** A growable Float64 series owned by one computation. */
export class Buf implements Series {
  data: Float64Array;

  constructor(capacity = 256) {
    this.data = new Float64Array(Math.max(1, capacity)).fill(Number.NaN);
  }

  at(i: number): number {
    return i >= 0 && i < this.data.length ? (this.data[i] ?? Number.NaN) : Number.NaN;
  }

  set(i: number, v: number): void {
    this.data[i] = v;
  }

  /** Grow (doubling) so index `n − 1` is addressable; new slots read NaN. */
  ensure(n: number): void {
    if (n <= this.data.length) return;
    let cap = this.data.length * 2;
    while (cap < n) cap *= 2;
    const next = new Float64Array(cap).fill(Number.NaN);
    next.set(this.data);
    this.data = next;
  }
}

/** A constant series (hline-like inputs, tests). */
export function constant(v: number): Series {
  return { at: (i) => (i >= 0 ? v : Number.NaN) };
}

/** Pine's `nz()`: NaN → `fallback` (default 0). */
export function nz(v: number, fallback = 0): number {
  return Number.isNaN(v) ? fallback : v;
}

/** Per-bar colour index meaning "the plot's default colour". */
export const DEFAULT_COLOR_INDEX = 255;

/**
 * Per-bar colour index meaning "default colour, and do not connect from the previous bar" (Pine
 * `plot.style_linebr` at an anchor reset: VWAP / CVD sessions). Lines, areas and fills start anew.
 */
export const LINE_BREAK = 254;

/** One plot's output: values plus an optional per-bar palette index. */
export class PlotBuf implements Series {
  values: Buf;
  colors: Uint8Array;

  constructor(capacity = 256) {
    this.values = new Buf(capacity);
    this.colors = new Uint8Array(Math.max(1, capacity)).fill(DEFAULT_COLOR_INDEX);
  }

  at(i: number): number {
    return this.values.at(i);
  }

  /** Write bar `i` (colour index into the plot's `colors` palette; omitted = default colour). */
  set(i: number, v: number, colorIndex: number = DEFAULT_COLOR_INDEX): void {
    this.values.data[i] = v;
    this.colors[i] = colorIndex;
  }

  colorAt(i: number): number {
    return this.colors[i] ?? DEFAULT_COLOR_INDEX;
  }

  ensure(n: number): void {
    this.values.ensure(n);
    if (n <= this.colors.length) return;
    const next = new Uint8Array(this.values.data.length).fill(DEFAULT_COLOR_INDEX);
    next.set(this.colors);
    this.colors = next;
  }
}
